import { Component, EventEmitter, Input, Output, ViewChild, DestroyRef, inject } from '@angular/core';
import { NgIf, NgStyle, NgTemplateOutlet, NgClass } from '@angular/common';
import { RouterLink, RouterLinkActive, NavigationEnd, Router } from '@angular/router';
import { MatSidenavContainer } from '@angular/material/sidenav';
import { User } from '../../../_interfaces/user';
import { MatIconModule } from '@angular/material/icon';
import { AuthService } from '../../../_services/auth.service';
import { BrandingService } from '../../../_services/branding.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { environment } from '../../../../environments/environment';
import { EMEA_REGION_CODES } from '../../../_enums/auth-server-environment';
import { resolveAvatarUrl, resolveSupportUrl } from '../../../_helpers/user.helper';
import { filter } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AnalyticsService } from '../../../_services/analytics.service';

@Component({
	selector: 'app-nav',
	standalone: true,
    imports: [NgIf, NgStyle, NgClass, RouterLink, RouterLinkActive, MatIconModule, NgTemplateOutlet, TranslateModule],
	templateUrl: './nav.component.html',
	styleUrl: './nav.component.scss'
})
export class NavComponent {
	user: User | null = null;
	@Output() logoutClick = new EventEmitter<void>();
	@ViewChild(MatSidenavContainer) container!: MatSidenavContainer;

	isCollapsed = false;
	private wasInDocument = false;
	private wasManuallyCollapsed = false;

	isLegalOrg = false;

	private readonly destroyRef = inject(DestroyRef);
	private readonly analytics = inject(AnalyticsService);

	constructor(
		private authService: AuthService,
		public branding: BrandingService,
		private translate: TranslateService,
		private router: Router,
	) {
		this.authService.user$.subscribe(user => {
			this.user = user;
			this.isLegalOrg = user?.entity?.settings?.category === 'Legal';
		});

		this.updateCollapsedState(this.router.url);

		this.router.events
		.pipe(
			filter((event): event is NavigationEnd => event instanceof NavigationEnd),
			takeUntilDestroyed(this.destroyRef),)
		.subscribe(event => {
		  this.updateCollapsedState(event.urlAfterRedirects);
		});
	}

	get showMdrInformation(): boolean {
		if (this.isLegalOrg) return false;
		return EMEA_REGION_CODES.has(environment.regionCode);
	}

	get isAiBlueprintsEnabled(): boolean {
		return this.authService.isAiBlueprintsEnabled;
	}

	get avatarUrl(): string | null {
		return resolveAvatarUrl(this.user?.profile?.avatar_url);
	}

	get supportUrl(): string {
		return resolveSupportUrl(this.user, this.translate.currentLang);
	}

	onLogout(): void {
		this.logoutClick.emit();
	}

	onSupportClick(): void {
		this.analytics.track('support_clicked', { entry_point: 'nav_bar' });
	}

	onNavClick(event: MouseEvent): void {
		const target = event.target as HTMLElement | null;
		if (target?.closest('a, button')) {
			return;
		}
		this.toggleCollapse();
	}

	onKeyDown(event: KeyboardEvent): void {
		const target = event.target as HTMLElement | null;
		if (target?.closest('a, button')) {
			return;
		}
		if (event.key === 'Enter') {
			event.preventDefault();
			this.toggleCollapse();
			return;
		}
		if (event.key === ' ') {
			event.preventDefault();
		}
	}

	onKeyUp(event: KeyboardEvent): void {
		const target = event.target as HTMLElement | null;
		if (target?.closest('a, button')) {
			return;
		}
		if (event.key === ' ') {
			event.preventDefault();
			this.toggleCollapse();
		}
	}

	toggleCollapse(): void {
		this.isCollapsed = !this.isCollapsed;
		if (this.wasInDocument) {
			this.wasManuallyCollapsed = this.isCollapsed;
		}
	}

	private updateCollapsedState(url: string): void {
		const isInDocument = url === '/document' || url.startsWith('/document/');

		// navigating into document
		if (isInDocument && !this.wasInDocument) {
			this.wasManuallyCollapsed = this.isCollapsed;
			this.isCollapsed = true;
		}
	
		// navigating from document
		if (!isInDocument && this.wasInDocument) {
			this.isCollapsed = this.wasManuallyCollapsed;
		}
	
		this.wasInDocument = isInDocument;
	}
} 